import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'

import { Switch } from '@/components/switch'

import { useCampaignClient } from '@/lib/api'
import { invalidateCampaignQueries } from '@/lib/cache-utils'
import CampaignButtons from '../../components/campaign-buttons'
import RadioGroupHappiness from '../../components/form/custom-radio-happiness-check'
import { RadioGroupHappinessInput } from '../../components/form/custom-types'
import { GetCampaignFragment } from '@/types/campaign'
import { activeStepState } from '../../store/atoms/active-step'

type HappinessCheckFormValues = {
  happinessCheckValue?: string
}

const cloneHappinessCheckForm = (
  form?: RadioGroupHappinessInput[] | null,
): RadioGroupHappinessInput[] | undefined =>
  form?.map((field) => ({
    ...field,
    options: field.options?.map((option) => ({ ...option })),
  }))

export default function HappinessCheck() {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const campaignClient = useCampaignClient()
  const { campaign } = useOutletContext<{ campaign: GetCampaignFragment }>()
  const setActiveStep = useSetRecoilState(activeStepState)
  const happinessCheckForm = useMemo<RadioGroupHappinessInput[]>(
    () => [
      {
        name: 'happinessCheck',
        type: 'custom',
        customControlType: 'radioGroupHappiness',
        iconTheme: 'faces',
        options: [
          {
            name: t('happiness_check.really_negative'),
            value: '1',
            Icon: 'bad',
          },
          {
            name: t('happiness_check.negative'),
            value: '2',
            Icon: 'belowAverage',
          },
          {
            name: t('happiness_check.alright'),
            value: '3',
            Icon: 'average',
          },
          {
            name: t('happiness_check.positive'),
            value: '4',
            Icon: 'aboveAverage',
          },
          {
            name: t('happiness_check.really_positive'),
            value: '5',
            Icon: 'good',
          },
        ],
      },
    ],
    [t],
  )
  const [selectedHappinessCheckForm, setSelectedHappinessCheckForm] = useState<
    RadioGroupHappinessInput[] | undefined
  >(() =>
    // null: never saved, start with the switch on. []: saved as turned off.
    Array.isArray(campaign.happinessCheck)
      ? campaign.happinessCheck.length
        ? cloneHappinessCheckForm(campaign.happinessCheck)
        : undefined
      : cloneHappinessCheckForm(happinessCheckForm),
  )

  const { control, handleSubmit, reset } = useForm<HappinessCheckFormValues>({
    defaultValues: {
      happinessCheckValue:
        selectedHappinessCheckForm?.[0]?.value !== undefined
          ? String(selectedHappinessCheckForm[0].value)
          : undefined,
    },
  })

  const setHappinessCheckFormMutation = useMutation({
    mutationFn: (data: { id: string; happinessCheck: any }) =>
      campaignClient.setHappinessCheckForm.mutate({
        params: { id: data.id },
        body: data,
      }),
  })

  useEffect(() => {
    const hasReferralIncentives =
      campaign?.referralCampaign && !campaign?.reuseSameIncentives
    // For non-referral campaigns, happiness check is step 6
    const stepNumber = campaign?.referralCampaign
      ? hasReferralIncentives
        ? 8
        : 7
      : 6
    setActiveStep(stepNumber)
  }, [setActiveStep, campaign?.referralCampaign, campaign?.reuseSameIncentives])

  const onSubmit = (draft: boolean) =>
    handleSubmit(({ happinessCheckValue }) => {
      if (!id) return

      const parsedValue =
        happinessCheckValue !== undefined && happinessCheckValue !== null
          ? Number(happinessCheckValue)
          : undefined

      const happinessCheckPayload = selectedHappinessCheckForm
        ? selectedHappinessCheckForm.map((field) => ({
            ...field,
            value: parsedValue !== undefined ? parsedValue : field.value,
          }))
        : []

      if (happinessCheckPayload.length) {
        setSelectedHappinessCheckForm(happinessCheckPayload)
      }

      setHappinessCheckFormMutation.mutate(
        {
          id,
          happinessCheck: happinessCheckPayload,
        },
        {
          onSuccess: () => {
            invalidateCampaignQueries(queryClient, id)

            if (!draft)
              navigate(`/campaigns/ec-flow/${campaign.id}/end-user/home`)
          },
          onError: (error) => {
            console.error('Failed to set happiness check form:', error)
          },
        },
      )
    })

  return (
    <div className="w-full mx-auto px-2">
      <h1>{t('happiness_check.module')}</h1>
      <div className="flex gap-4 justify-start items-center py-4">
        <Switch
          checked={!!selectedHappinessCheckForm}
          onCheckedChange={(showHappinessCheck) => {
            if (showHappinessCheck) {
              const clonedForm = cloneHappinessCheckForm(happinessCheckForm)
              setSelectedHappinessCheckForm(clonedForm)
              reset({
                happinessCheckValue:
                  clonedForm?.[0]?.value !== undefined
                    ? String(clonedForm[0].value)
                    : undefined,
              })
            } else {
              setSelectedHappinessCheckForm(undefined)
              reset({ happinessCheckValue: undefined })
            }
          }}
          data-testid="toggle-happiness-check"
        />
        <p>{t('happiness_check.include')}</p>
      </div>
      {selectedHappinessCheckForm && (
        <div className="bg-ui-100 items-center justify-center py-12 min-w-[640px]">
          <RadioGroupHappiness
            name="happinessCheckValue"
            control={control}
            options={selectedHappinessCheckForm[0]?.options}
            iconTheme={selectedHappinessCheckForm[0]?.iconTheme}
            disabled
            defaultValue={selectedHappinessCheckForm[0]?.value}
          />
        </div>
      )}

      <CampaignButtons
        onSaveDraft={onSubmit(true)}
        onSubmit={onSubmit(false)}
        submitLabel={t('save_campaign')}
      />
    </div>
  )
}
